import {
  all, call, put, select, take,
} from 'redux-saga/effects';
import {
  forEach, getOr, map, get,
} from 'lodash/fp';

import * as adminServices from '../admin/services';
import { FIREBASE_REF } from '../common/constants';
import firebase from '../common/firebase';
import * as commonServices from '../common/services';
import * as errorActions from '../errors/actions';
import * as actions from './actions';

function* fetchDraftFile({ did, sid }) {
  try {
    const ref = yield call(firebase.storage.ref, `${FIREBASE_REF}/schools/${sid}/drafts/${did}.pdf`);

    const [downloadURL, metadata] = yield all([
      call(firebase.storage.getDownloadURL, ref),
      call(firebase.storage.getMetadata, ref),
    ]);

    const { data: blob } = yield call(commonServices.fetchBlob, downloadURL);

    blob.name = getOr('', 'customMetadata.name', metadata);

    yield put(actions.fetchDraftFileSuccess({ blob }));
  } catch (error) {
    if (get('code', error) === 'storage/object-not-found') {
      return;
    }
    yield put(actions.fetchDraftFileFailure({ error }));

    yield put(errorActions.errorOccured({ error }));
  }
}

export function* fetchDraft() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_DRAFT_REQUEST);

      const { sid, did } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const [draftRef, draftSurveyRef] = yield all([
        call(firebase.firestore.doc, `${FIREBASE_REF}/drafts/${did}`),
        call(firebase.firestore.doc, `${FIREBASE_REF}/draft-surveys/${did}`),
      ]);

      const [draftDoc, draftSurveyDoc] = yield all([
        call([draftRef, 'get']),
        call([draftSurveyRef, 'get']),
      ]);

      yield call(fetchDraftFile, { sid, did });

      const draft = draftDoc.exists ? draftDoc.data() : null;
      const draftSurvey = draftSurveyDoc.exists ? draftSurveyDoc.data() : null;

      yield put(actions.fetchDraftSuccess({ draft: { id: did, ...draft, ...draftSurvey } }));
    } catch (error) {
      yield put(actions.fetchDraftFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchDrafts() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_DRAFTS_REQUEST);

      const { sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const ref = yield call(firebase.firestore.collection, `${FIREBASE_REF}/drafts`);

      const snapshot = yield call(
        firebase.firestore.getCollection,
        ref.where('school', '==', sid).orderBy('updatedAt', 'desc'),
      );

      const drafts = map(doc => ({
        id: doc.id,
        writer: '-',
        ...doc.data(),
      }), snapshot.docs);

      yield put(actions.fetchDraftsSuccess({ drafts }));
    } catch (error) {
      yield put(actions.fetchDraftsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* createDraft() {
  while (true) {
    try {
      const action = yield take(actions.CREATE_DRAFT_REQUEST);

      const { values, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const writer = yield select(state => state.user.info.name);

      const updatedAt = yield call(firebase.firestore.FieldValue.serverTimestamp);

      const draft = {
        school: sid,
        title: values.title,
        description: values.description,
        number: values.number,
        writer,
        updatedAt,
      };

      const collectionRef = yield call(firebase.firestore.collection, `${FIREBASE_REF}/drafts`);

      const { id: did } = yield call(firebase.firestore.addDocument, collectionRef, draft);

      const docRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/draft-surveys/${did}`);
      const storageRef = yield call(firebase.storage.ref, `${FIREBASE_REF}/schools/${sid}/drafts/${did}.pdf`);

      yield all([
        call(
          firebase.firestore.setDocument,
          docRef,
          { questions: values.questions, signatures: values.signatures },
          { merge: true },
        ),
        values.file && call(
          firebase.storage.put,
          storageRef,
          values.file,
          { customMetadata: { name: getOr('', 'name', values.file) } },
        ),
      ]);

      yield put(actions.createDraftSuccess({ draft: did }));
    } catch (error) {
      yield put(actions.createDraftFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* updateDraft() {
  while (true) {
    try {
      const action = yield take(actions.UPDATE_DRAFT_REQUEST);

      const { values, sid, did } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const writer = yield select(state => state.user.info.name);

      const updatedAt = yield call(firebase.firestore.FieldValue.serverTimestamp);

      const draft = {
        school: sid,
        title: values.title,
        description: values.description,
        number: values.number,
        writer,
        updatedAt,
      };

      const draftRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/drafts/${did}`);
      const draftSurveyRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/draft-surveys/${did}`);
      const storageRef = yield call(firebase.storage.ref, `${FIREBASE_REF}/schools/${sid}/drafts/${did}.pdf`);

      yield all([
        call(
          firebase.firestore.setDocument,
          draftRef,
          draft,
          { merge: true },
        ),
        call(
          firebase.firestore.setDocument,
          draftSurveyRef,
          { questions: values.questions, signatures: values.signatures },
          { merge: true },
        ),
        values.file && call(
          firebase.storage.put,
          storageRef,
          values.file,
          { customMetadata: { name: getOr('', 'name', values.file), foo: 'hello' } },
        ),
      ]);
      yield put(actions.updateDraftSuccess());
    } catch (error) {
      yield put(actions.updateDraftFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

function* deleteDraftFile({ did, sid }) {
  try {
    const ref = yield call(firebase.storage.ref, `${FIREBASE_REF}/schools/${sid}/drafts/${did}.pdf`);

    yield call(
      firebase.storage.delete,
      ref,
    );
  } catch (error) {
    if (get('code', error) === 'storage/object-not-found') {
      return;
    }

    throw error;
  }
}

export function* deleteDraft() {
  while (true) {
    try {
      const action = yield take(actions.DELETE_DRAFT_REQUEST);

      const { did, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const batch = yield call(firebase.firestore.batch);

      const refs = yield all([
        call(firebase.firestore.doc, `${FIREBASE_REF}/drafts/${did}`),
        call(firebase.firestore.doc, `${FIREBASE_REF}/draft-surveys/${did}`),
      ]);

      forEach(ref => batch.delete(ref), refs);

      yield all([
        call([batch, 'commit']),
        call(deleteDraftFile, { did, sid }),
      ]);

      yield put(actions.deleteDraftSuccess({ did }));
    } catch (error) {
      yield put(actions.deleteDraftFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* deleteDrafts() {
  while (true) {
    try {
      const action = yield take(actions.DELETE_DRAFTS_REQUEST);

      const { dids, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const batch = yield call(firebase.firestore.batch);

      const refs = yield all([...map(did => call(
        firebase.firestore.doc,
        `${FIREBASE_REF}/drafts/${did}`,
      ), dids), ...map(did => call(
        firebase.firestore.doc,
        `${FIREBASE_REF}/draft-surveys/${did}`,
      ), dids)]);

      forEach(ref => batch.delete(ref), refs);

      yield all([
        call([batch, 'commit']),
        ...map(did => call(
          deleteDraftFile, { did, sid },
        ), dids),
      ]);

      yield put(actions.deleteDraftsSuccess({ dids }));
    } catch (error) {
      yield put(actions.deleteDraftsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}
